import { Fragment, useEffect, useRef, useState } from 'react'
import { sortBy } from 'lodash-es'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'

import {
  IEventTableAttributes,
  IEventTableMeta,
  useGetEventTablesForExportQuery
} from 'app/services/api/datamanagement/eventTableAttributes'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { useExportJobContext } from 'common/contexts/useExportJobContext'

import tailwind from 'styles/themeTokens'

import AttributeSetSelector from './AttributeSetSelector'
import DataExportButton from './DataExportButton'

enum DatasetSelectionType {
  CurrentDataset = 'currentdataset',
  FullDataset = 'fulldataset'
}

const DataExport = () => {
  const [exportDropdownSelection, setExportDropdownSelection] = useState<
    string[]
  >([])

  const [selectedEventTables, setSelectedEventTables] = useState<
    IEventTableMeta[]
  >([])

  const [exportOption, setExportOption] = useState<DatasetSelectionType>(
    DatasetSelectionType.CurrentDataset
  )

  const { currentData: exportEventTables = [] } =
    useGetEventTablesForExportQuery()

  const { isExporting } = useExportJobContext()
  const prevIsExportingRef = useRef(false)

  useEffect(() => {
    if (prevIsExportingRef.current && !isExporting) {
      setExportDropdownSelection([])
      setSelectedEventTables([])
    }

    prevIsExportingRef.current = isExporting
  }, [isExporting])

  // Dropdown value selection
  const onChange = (selection: string[]) => {
    setSelectedEventTables(
      selection.map(
        (tableName) =>
          exportEventTables.find(
            (exportEventTable) => exportEventTable.name === tableName
          ) as IEventTableMeta
      )
    )
    setExportDropdownSelection(selection)
  }

  const exportParams = {
    eventTableList: selectedEventTables,
    tableType: 'fullTableType',
    exportDate: new Date().toJSON(),
    exportOption
  }

  return (
    <div className='mt-4 flex flex-1'>
      <OverlayScrollbar>
        <div className='flex'>
          <div className='mx-10 mt-5 max-h-full w-1/3 2xl:ml-24'>
            <div className='mb-3 font-semibold'>Select Attribute Sets</div>
            <AttributeSetSelector
              eventTables={exportEventTables}
              selectedValues={exportDropdownSelection}
              onChange={onChange}
            />
            <div className='mb-3 mt-6 font-semibold'>Export Options</div>
            <div className='ml-3'>
              <RadioGroup
                aria-labelledby='demo-radio-buttons-group-label'
                defaultValue='currentdataset'
                name='radio-buttons-group'
                onChange={(e) => {
                  setExportOption(e.target.value as DatasetSelectionType)
                }}
              >
                <FormControlLabel
                  value={DatasetSelectionType.CurrentDataset}
                  control={<Radio />}
                  label='Active Data Set'
                  sx={{
                    // style form control label
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px',
                      color: tailwind.theme.colors.black
                    },
                    '& .MuiButtonBase-root': {
                      pl: '0.5rem',
                      pr: '0.75rem'
                    }
                  }}
                />
                <div className='mb-3 pl-8 text-sm italic text-gray-700'>
                  Export the active data set to edit and import
                </div>
                <FormControlLabel
                  value={DatasetSelectionType.FullDataset}
                  control={<Radio />}
                  label='Entire Data Set'
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      fontSize: '14px',
                      color: tailwind.theme.colors.black
                    },
                    '& .MuiButtonBase-root': {
                      pl: '0.5rem',
                      pr: '0.75rem'
                    }
                  }}
                />
                <div className='mb-3 pl-8 text-sm italic text-gray-700'>
                  Export the entire data set including historic data
                </div>
              </RadioGroup>
            </div>
            <DataExportButton exportParams={exportParams} />
          </div>
          <div className='w-full bg-white pl-10 pr-28'>
            <div className='mb-8 ml-3 mt-5 font-medium uppercase tracking-[0.03em] text-gray-500'>
              Review Data For Export
            </div>
            <div className=''>
              <div className='mb-3 grid grid-cols-5 font-semibold tracking-[0.02em]'>
                <div className='col-span-2'>
                  <div className='ml-3'>Attribute Set</div>
                </div>
                <div className='col-span-3'>
                  <div className='ml-3'>Included Attributes</div>
                </div>
              </div>
              <hr className='my-2 border-b border-gray-300 text-gray-300' />
              <div>
                {selectedEventTables.length > 0 ? (
                  sortBy(selectedEventTables, 'name').map(
                    (table: IEventTableMeta) => {
                      return (
                        <Fragment key={table.id}>
                          <div className='grid grid-cols-5'>
                            <div className='col-span-2 py-2 font-medium'>
                              <div className='ml-3'>{table.name}</div>
                            </div>
                            <div className='col-span-3 max-w-xl py-2'>
                              <div className='ml-3'>
                                {sortBy(table.eventTableAttributes, [
                                  (i) => i.name.toLowerCase(),
                                  (i) => i.name
                                ])
                                  .map((attr: IEventTableAttributes) =>
                                    attr.name.trim()
                                  )
                                  .join(', ')}
                              </div>
                            </div>
                          </div>
                          <hr className='my-2 text-gray-300' />
                        </Fragment>
                      )
                    }
                  )
                ) : (
                  <div className='rounded-sm border border-gray-300 bg-gray-100 px-6 py-4'>
                    No data set selected. Use the options on the left to select
                    data sets, and review what will be included in the export.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </OverlayScrollbar>
    </div>
  )
}

export default DataExport
